import type { SignInOutput } from '@aws-amplify/auth'
import { getCurrentUser } from '@aws-amplify/auth'
import { Spin } from 'antd'
import { useFormik } from 'formik'
import queryString from 'query-string'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Form, FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Heading } from 'sutra/components/Text/Heading'
import { Text } from 'sutra/components/Text/Text'
import * as Yup from 'yup'

import { handleException, runOperation } from 'clients/exception-handling'
import { Loader } from 'components'
import { useAuthContext } from 'features/auth'
import { AuthWrapper } from 'features/auth/components/AuthWrapper'
import { FormSent } from 'features/auth/components/FormSent'
import { InputPassword } from 'features/auth/components/Input'
import { isAuthErrorNamed } from 'features/auth/expected-auth-error'
import { useAuthService } from 'features/auth/service'
import type { User } from 'features/auth/types/auth'
import { setUpPassword, signIn } from 'features/auth/utils'
import { HOME } from 'features/projects/paths'
import mixpanel from 'trackers/mixpanel'

const SUPPORT_EMAIL = 'support@meetyogi.com'

// Amplify raises this when the temporary password is wrong or expired — the
// recoverable case that triggers the resend flow. Any other sign-in error is
// treated as non-recoverable so a still-valid temp password is never
// invalidated by an unnecessary resend.
const EXPIRED_TEMP_PASSWORD_ERROR = 'NotAuthorizedException'

// Amplify reports the discriminating detail on `name`, not the message, and
// several failures here never reach Cognito at all — so the name is the only
// signal that separates them.
const errorName = (error: unknown): string =>
  (error as { name?: string })?.name ?? 'unknown'

const SetupPasswordSchema = Yup.object().shape({
  password: Yup.string().required('Password is required'),
  passwordConfirmation: Yup.string()
    .oneOf([Yup.ref('password')], "Passwords don't match")
    .required('Confirm Password is required'),
})

const EmailSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
})

// The page has one job in three situations:
//  - `password`: a valid activation link — let the user set their password.
//  - `resend`:   an expired/invalid link but we know the email — auto-resend a
//                fresh activation email and confirm it (Chad's flow).
//  - `enter-email`: opened with no link params — ask for the email, then resend
//                (the recovery route support can send lost-email users to).
type Stage =
  | 'signing-in'
  | 'password'
  | 'auto-resending'
  | 'auto-resent'
  | 'enter-email'
  | 'manual-resending'
  | 'manual-resent'
  | 'error'

const parseActivationEmail = (payload: string | undefined): string => {
  if (typeof payload !== 'string') return ''
  try {
    const parsed = JSON.parse(atob(payload)) as { email?: string }
    return parsed.email ?? ''
  } catch (cause) {
    handleException(cause, {
      operation: 'auth.activation.parse',
      disposition: 'expected',
      isExpectedError: (cause) =>
        cause instanceof SyntaxError ||
        (cause instanceof DOMException &&
          cause.name === 'InvalidCharacterError'),
      reason:
        'Malformed activation data asks the visitor to enter their email.',
    })

    return ''
  }
}

export const SetupPasswordForm = (): JSX.Element => {
  const authContext = useAuthContext()
  // bully ignore[no-service-hooks-outside-containers: route-level auth form owns its own wiring (no Container/View split in features/auth/forms), same shape as the sibling SignupTrialForm; refactor: when the auth forms migrate to the Container/View pattern]
  const authService = useAuthService()
  const location = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [cognitoUser, setCognitoUser] = useState<SignInOutput | null>(null)
  const [confirmedEmail, setConfirmedEmail] = useState('')

  const queryParams = useMemo(
    () => queryString.parse(location.search),
    [location.search],
  )
  const queryCode = queryParams['code']
  const activationEmail = useMemo(
    () => parseActivationEmail(queryParams['data'] as string | undefined),
    [queryParams],
  )
  const hasActivationLink =
    typeof queryCode === 'string' && activationEmail.length > 0

  const [stage, setStage] = useState<Stage>(
    hasActivationLink ? 'signing-in' : 'enter-email',
  )

  const passwordFormik = useFormik({
    initialValues: { password: '', passwordConfirmation: '' },
    validationSchema: SetupPasswordSchema,
    onSubmit: async ({ password }) => {
      try {
        if (!cognitoUser) return
        setLoading(true)
        await setUpPassword(cognitoUser, password)
        const user = await getCurrentUser()
        if (activationEmail) {
          void runOperation(() => mixpanel.identify(activationEmail), {
            operation: 'auth.analytics.activation',
            recover: {
              reason: 'Password setup continues without optional analytics.',
              run: () => undefined,
            },
          })
        }
        await authContext.signIn(user as User)
        toast.info('Success')
        navigate(HOME)
      } catch (e) {
        handleException(e, {
          operation: 'auth.password.setup',
          disposition: 'expected',
          isExpectedError: (cause) =>
            isAuthErrorNamed(
              cause,
              'InvalidPasswordException',
              'NotAuthorizedException',
              'LimitExceededException',
            ),
          reason: 'Identity provider rejected the requested password.',
        })

        setLoading(false)

        toast.error((e as Error).message)
      } finally {
        setLoading(false)
      }
    },
  })

  const emailFormik = useFormik({
    initialValues: { email: '' },
    validationSchema: EmailSchema,
    onSubmit: async ({ email }) => {
      setStage('manual-resending')
      try {
        await authService.resendAccountActivation(email)
        setConfirmedEmail(email)
        setStage('manual-resent')
      } catch (e) {
        handleException(e, {
          operation: 'auth.activation.resend',
          disposition: 'failed',
        })

        setStage('error')
      }
    },
  })

  // Attempt the auto sign-in exactly once when the page carries an activation
  // link. On the expired-password error we auto-resend a fresh activation email
  // (Chad's flow); any other failure is non-recoverable. The ref guards against
  // React StrictMode's double-invoked effect so we never resend twice.
  const signInAttempted = useRef(false)
  useEffect(() => {
    if (!hasActivationLink || signInAttempted.current) return
    signInAttempted.current = true

    signIn(activationEmail, queryCode as string)
      .then((user) => {
        setCognitoUser(user)
        setStage('password')
      })
      .catch((error: unknown) => {
        handleException(error, {
          operation: 'auth.activation.sign-in',
          disposition: 'expected',
          isExpectedError: (cause) =>
            isAuthErrorNamed(cause, 'NotAuthorizedException'),
          reason: 'Expired invitation enters the resend recovery flow.',
        })

        const isExpired = errorName(error) === EXPIRED_TEMP_PASSWORD_ERROR
        if (!isExpired) {
          setStage('error')
          return
        }

        setStage('auto-resending')
        authService
          .resendAccountActivation(activationEmail)
          .then(() => setStage('auto-resent'))
          .catch((e: unknown) => {
            handleException(e, {
              operation: 'auth.activation.auto-resend',
              disposition: 'failed',
            })

            setStage('error')
          })
      })
  }, [hasActivationLink, activationEmail, queryCode, authService])

  const passwordError =
    passwordFormik.touched.password && passwordFormik.errors.password
  const passwordConfirmationError =
    passwordFormik.touched.passwordConfirmation &&
    passwordFormik.errors.passwordConfirmation
  const emailError = emailFormik.touched.email && emailFormik.errors.email

  const renderBody = (): JSX.Element => {
    if (stage === 'auto-resending' || stage === 'manual-resending') {
      return (
        <Centered>
          <Spin size="large" />
          <Sending>Sending a new activation email...</Sending>
        </Centered>
      )
    }

    if (stage === 'auto-resent') {
      return (
        <Message>
          We&apos;re sorry. This link is no longer valid. We sent you a new
          account activation email. If you continue to have issues, please reach
          out to us at <SupportLink />.
        </Message>
      )
    }

    if (stage === 'manual-resent') {
      return (
        <FormSent
          email={confirmedEmail}
          linkNoun="a new account activation email"
        />
      )
    }

    if (stage === 'enter-email') {
      return (
        <EmailForm onFinish={() => emailFormik.submitForm()} layout="vertical">
          <Message>
            We&apos;re sorry. This link is no longer valid. Please enter your
            email to receive a new account activation email. If you continue to
            have issues, please reach out to us at <SupportLink />.
          </Message>
          <FormItem
            label="Email"
            validateStatus={emailError ? 'error' : undefined}
            help={emailError}
          >
            <Input
              $error={!!emailError}
              name="email"
              autoComplete="username"
              placeholder="john.smith@acmelabs.com"
              onChange={emailFormik.handleChange}
              value={emailFormik.values.email}
            />
          </FormItem>
          <SubmitButton variant="primary" type="submit" block>
            Send activation email
          </SubmitButton>
        </EmailForm>
      )
    }

    if (stage === 'error') {
      return (
        <Error>
          Something went wrong. Please try again, or contact <SupportLink /> for
          assistance.
        </Error>
      )
    }

    // 'signing-in' | 'password'
    return (
      <PasswordForm
        layout="horizontal"
        onFinish={() => passwordFormik.submitForm()}
        labelCol={{ span: 7 }}
      >
        <FormItem
          label="New password"
          validateStatus={passwordError ? 'error' : undefined}
          help={passwordError}
        >
          <InputPassword
            error={!!passwordError}
            name="password"
            autoComplete="current-password"
            onChange={passwordFormik.handleChange}
            value={passwordFormik.values.password}
            disabled={!cognitoUser}
          />
        </FormItem>
        <FormItem
          label="Confirmation"
          validateStatus={passwordConfirmationError ? 'error' : undefined}
          help={passwordConfirmationError}
        >
          <InputPassword
            error={!!passwordConfirmationError}
            name="passwordConfirmation"
            autoComplete="current-password"
            onChange={passwordFormik.handleChange}
            value={passwordFormik.values.passwordConfirmation}
            disabled={!cognitoUser}
          />
        </FormItem>
        {loading && (
          <Overlay>
            <Loader />
          </Overlay>
        )}
        <LastFormItem>
          <PasswordSubmitButton
            variant="primary"
            type="submit"
            disabled={!cognitoUser}
          >
            Submit
          </PasswordSubmitButton>
        </LastFormItem>
      </PasswordForm>
    )
  }

  return (
    <AuthWrapper>
      <Wrapper>
        <FormTitle>
          <Heading as="h4" $level="5">
            Create your password
          </Heading>
          {/* The password-rules help only applies while the user is actually
              setting a password — hide it on the resend / enter-email recovery
              views, where it's just noise. */}
          {(stage === 'signing-in' || stage === 'password') && (
            <Text as="span" $size="inherit" $tone="inherit">
              Please enter a password that is at least 8 characters long and
              contains at least 1 lowercase character, 1 uppercase character, 1
              special character, and 1 number.
            </Text>
          )}
        </FormTitle>
        {renderBody()}
      </Wrapper>
    </AuthWrapper>
  )
}

const SupportLink = (): JSX.Element => (
  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
)

const Wrapper = styled.div`
  max-width: 500px;
  margin: 0 auto;
  box-shadow: var(--default-box-shadow);
  padding: var(--default-padding);
  position: relative;
  background: #fff;

  @media (max-width: 768px) {
    max-width: none;
    margin: var(--mobile-margin);
  }
`
const PasswordForm = styled(Form)`
  position: relative;
`
const EmailForm = styled(Form)`
  position: relative;
`
const FormTitle = styled.div`
  text-align: center;
  margin-bottom: var(--default-padding);

  & > h4 {
    margin-bottom: 10px;
  }
`
const LastFormItem = styled(FormItem)`
  margin-bottom: 0;
`
const SubmitButton = styled(Button)`
  margin-top: var(--default-padding);
`
const PasswordSubmitButton = styled(SubmitButton)`
  display: flex;
  width: fit-content;
  margin-left: auto;
`
const Overlay = styled.div`
  background: #fff;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`
const Message = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 16px;
`
const Error = styled.div`
  color: #ff4d4f;
  width: 100%;
  text-align: center;
  margin-bottom: 16px;
`
const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px 0;
`
const Sending = styled.div`
  color: var(--color-auth-grey);
  font-size: 16px;
`
